import { useReduxLoading } from '@core/hooks';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Grid, makeStyles } from '@material-ui/core';
import * as Actions from 'app/store/actions';
import { useFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ConciliacaoContext } from '../ConciliacaoContext';
import {
  BotaoConciliar,
  BotaoDesconciliar,
  ContainerExtratoForm,
} from '../containers/ContainerLancamentoExtratoBancario';
import FormularioTransferencia from '../forms/FormularioTransferencia';
import { ConciliacaoModal } from '../modal/ConciliacaoModal';
import PesquisaTransferenciaModal from '../pesquisas/transferencia/PesquisaTransferenciaModal';
import VisualizacaoTransferencia from '../visualizacao/VisualizacaoTransferencia';

const useStyles = makeStyles((theme) => ({
  btnContainer: {
    alignSelf: 'center',
  },
}));

const ContentLancamentoTransferencia = ({ extrato, handlerConciliar, handlerDesconciliar }) => {
  const [formularioTransferencia, setFormularioTransferencia] = useContext(ConciliacaoContext).formularioTransferencia;
  const [loading, setLoading] = useState(false);
  const [conciliacoes, setConciliacoes] = useContext(ConciliacaoContext).conciliacoes;
  const [modalConciliacao, setModalConciliacao] = useContext(ConciliacaoContext).modal;
  const [extratoSelecionado, setExtratoSelecionado] = useContext(ConciliacaoContext).extratoSelecionado;

  useReduxLoading(loading);

  const dispatch = useDispatch();

  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  const classes = useStyles();

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    contaOrigemId: registro?.contaOrigem?.id,
    contaDestinoId: registro?.contaDestino?.id,
    valor: registro?.valor,
    data: registro?.data,
    descricao: registro?.descricao,
    tipoLancamento: registro?.tipoLancamento || 'TRANSFERENCIA',
  });

  const FormTransferenciaSchema = yup.object().shape({
    contaOrigem: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a conta de origem')),
    contaDestino: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a conta de destino')),
  });

  const handlerConciliarTransferencia = () => {
    let conciliacaoAux = conciliacoes.filter((c) => c.id === extrato.id)[0];
    conciliacaoAux.transferenciaBancariaForm = {
      ...formik.values,
      id: formik.values?.id,
      origemId: formik.values?.contaOrigem?.id,
      destinoId: formik.values?.contaDestino?.id,
      valor: formik.values?.valor,
      data: formik.values?.data,
      descricao: formik.values?.descricao,
    };

    if (formik.values?.contaOrigem?.id === formik.values?.contaDestino?.id) {
      showError('Não é permitido realizar transferência para a mesma conta.');
    } else {
      handlerConciliar(conciliacaoAux);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(formularioTransferencia),
    validationSchema: FormTransferenciaSchema,
    onSubmit: handlerConciliarTransferencia,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { resetForm } = formik;
  useEffect(() => {
    resetForm(getInitialValues(formularioTransferencia));
  }, [formularioTransferencia, resetForm]);

  const handlerLimparInformacoes = () => {
    if (extrato.transferenciaBancaria?.id) {
      handlerLimparInformacoesVisualizacao();
    } else {
      handlerDesfazerTransferencia();
    }
  };

  const handlerDesfazerTransferencia = () => {
    setLoading(true);

    formik.resetForm(getInitialValues(formularioTransferencia));

    setConciliacoes(
      conciliacoes.map((c) => {
        if (c.id === extrato.id) {
          c.criarTransferencia = false;
        }
        return c;
      })
    );

    setLoading(false);
  };

  const handlerLimparInformacoesVisualizacao = () => {
    setLoading(true);
    delete extrato.transferenciaBancaria;
    setExtratoSelecionado(extrato);
    carregaTransferenciaNoCard();
    setLoading(false);
  };

  const carregaTransferenciaNoCard = () => {
    conciliacoes
      .filter((c) => c.id === extratoSelecionado.id)
      .forEach((c) => {
        c.transferenciaBancaria = extratoSelecionado.transferenciaBancaria;
        c.criarTransferencia = true;
      });
    setConciliacoes(conciliacoes);
    setExtratoSelecionado({});
  };

  const handlerPesquisar = () => {
    setLoading(true);
    setExtratoSelecionado(extrato);
    setModalConciliacao(ConciliacaoModal.pesquisaTransferencia);
    setLoading(false);
  };

  return extrato && (extrato.criarTransferencia || extrato?.transferenciaBancaria) ? (
    <>
      <Grid item xs={2} className={classes.btnContainer}>
        {!extrato?.id ? null : extrato.conciliado !== true ? (
          <BotaoConciliar
            onClick={() => {
              extrato.transferenciaBancaria ? handlerConciliar(extrato) : formik.submitForm();
            }}
          />
        ) : (
          <BotaoDesconciliar
            onClick={() => {
              handlerDesconciliar(extrato);
            }}
          />
        )}
      </Grid>
      <Grid item xs={5}>
        <ContainerExtratoForm
          values={extrato}
          handlerLimparInformacoes={handlerLimparInformacoes}
          handlerPesquisar={handlerPesquisar}
          formTransferencia={formik?.values?.tipoLancamento === 'TRANSFERENCIA'}
          temPermissaoParaVisualizar={permissaoUtils.transferencia().visualizar}
          temPermissaoParaEditar={permissaoUtils.transferencia().editar}
          temPermissaoParaCriar={permissaoUtils.transferencia().cadastrar}
        >
          {!extrato?.transferenciaBancaria ? (
            <FormularioTransferencia formik={formik} extrato={extrato} />
          ) : (
            <VisualizacaoTransferencia
              lancamento={extrato.transferenciaBancaria}
              exibirTooltipSemCorrespondencia={extrato.valor === 0}
            />
          )}
          {modalConciliacao === ConciliacaoModal.pesquisaTransferencia && (
            <PesquisaTransferenciaModal setFormularioTransferencia={setFormularioTransferencia} />
          )}
        </ContainerExtratoForm>
      </Grid>
    </>
  ) : null;
};

export default ContentLancamentoTransferencia;
